import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'next-i18next'
import useLangPrefix from '@/hooks/useLangPrefix'
import { Skeleton, Flex } from '@mantine/core'
import { eventTracker } from '@/utils/collectEvent'
import { useStore } from '@/store/index'
import { Button, Divider } from '@gate/gui'
import { formatTimeCode, deepClone } from '@/utils/tools'
import BaseEmpty from '@/components/BaseEmpty'
import BaseSuspense from '@/components/BaseSuspense'
import {
  PriceFutureStyled,
  PriceFutureItemBox,
  PriceFutureItemFlex,
  PriceFutureContainerStyled,
  PriceFutureTitle,
} from './style'

const FutureEventLiItem = React.lazy(() => import('@/blocks/Price/PriceFuture/Item'))

const PriceFuture = () => {
  const { priceClientData } = useStore().baseStore

  const { t } = useTranslation()
  const { langPrefix } = useLangPrefix()

  const { loading, futureEventListData } = priceClientData

  const featureEventDealData = useMemo(() => {
    const _data = (deepClone(futureEventListData) ?? []).map(ite => {
      return { ...ite, pub_time_format: formatTimeCode(ite?.pub_time * 1000) }
    })

    const group2DData = groupByTo2DArray(_data, 'pub_time_format')

    return group2DData
  }, [futureEventListData])

  /**
   * @description: 未来事件 | 骨架屏
   * @param {*} React
   * @return {*}
   */
  const PriceFutureItemSkeleton = ({ showDaySvg }: { showDaySvg?: boolean }): React.ReactElement => {
    return (
      <PriceFutureItemBox>
        {showDaySvg ? <Skeleton w={122} h={38} radius={6} /> : null}
        <PriceFutureItemFlex>
          <Flex gap={8} align="center" w={{ base: '100%', sm: 140 }}>
            <Skeleton w={32} h={32} style={{ borderRadius: '50%' }} />
            <Flex w={{ base: '100%', sm: 68 }} justify="center">
              <Skeleton w="100%" h={19} />
            </Flex>
          </Flex>
          <Flex gap={8} direction="column" style={{ width: '100%' }}>
            <Flex justify="space-between" mb={6} align="center">
              <Skeleton w={162} h={19} />
              <Skeleton w={97} h={24} />
            </Flex>
            <Skeleton w="100%" h={40} />
          </Flex>
        </PriceFutureItemFlex>
      </PriceFutureItemBox>
    )
  }
  return (
    <PriceFutureStyled>
      <PriceFutureContainerStyled>
        <Flex align="center" justify="space-between" mb={24}>
          <PriceFutureTitle>{t('P.Upcoming')}</PriceFutureTitle>
          <a
            href={`${langPrefix}/live-news/upcoming-event`}
            target="_blank"
            onClick={() => {
              eventTracker('price_sectors_click', {
                button_name: 'Upcoming_more',
              })
            }}
          >
            <Button size="xs" type="outlined" label={t('P.More') as string} className="text-1 size-12 font-500" />
          </a>
        </Flex>

        <BaseSuspense
          loading={loading}
          fallback={
            <>
              <PriceFutureItemSkeleton showDaySvg={true} />
              <PriceFutureItemSkeleton />
              <Divider mb={24} type="divider-2" style={{ width: '98%' }} />
              <PriceFutureItemSkeleton showDaySvg={true} />
            </>
          }
        >
          <ul>
            {futureEventListData?.length ? (
              <>
                {featureEventDealData?.map((item, index) => {
                  return (
                    <FutureEventLiItem
                      borderState={
                        index + 1 !== futureEventListData?.length &&
                        Array.from(new Set(futureEventListData?.map(currentItem => currentItem?.pub_time)))?.length !==
                          1 // 判断有无其他天的新闻
                      }
                      key={index}
                      dataItem={item}
                    />
                  )
                })}
              </>
            ) : (
              <Flex py={48} mih={491} justify="center" align="center">
                <BaseEmpty />
              </Flex>
            )}
          </ul>
        </BaseSuspense>
      </PriceFutureContainerStyled>
    </PriceFutureStyled>
  )
}

function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((result: Record<string, T[]>, currentValue: T) => {
    // Get the value of the key we are grouping by

    const groupKey = currentValue[key] as unknown as string

    // If this group does not exist yet, create it
    if (!result[groupKey]) {
      result[groupKey] = []
    }

    // Add the current value to the group
    result[groupKey].push(currentValue)

    return result
  }, {})
}

function groupByTo2DArray<T>(array: T[], key: keyof T): T[][] {
  const groupedObj = groupBy(array, key)
  return Object.values(groupedObj)
}

export default observer(PriceFuture)
